import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * Componente de gráfico de tendencias de métricas
 * Muestra la evolución temporal de diferentes métricas
 */
const MetricsTrendChart = ({ 
  data, 
  metrics = ['sessions', 'quotations', 'contractions', 'certificates'],
  isLoading = false,
  className = "",
  timeframe = '30d',
  showLegend = true
}) => {
  /* Multi-series: cada métrica usa su color semántico Yool */
  const metricConfig = {
    sessions:      { label: 'Sesiones',       color: 'var(--fy-sky)',    strokeWidth: 2 },
    quotations:    { label: 'Cotizaciones',   color: 'var(--fy-sky)',    strokeWidth: 2 },
    contractions:  { label: 'Contrataciones', color: 'var(--fy-teal)',   strokeWidth: 2 },
    certificates:  { label: 'Certificados',   color: 'var(--fy-teal)',   strokeWidth: 2 },
    subscriptions: { label: 'Suscripciones',  color: 'var(--fy-violet)', strokeWidth: 2 },
    payments:      { label: 'Pagos',          color: 'var(--fy-orange)', strokeWidth: 2 },
  };

  const formatData = (trendData) => {
    if (!trendData || !Array.isArray(trendData)) return [];

    return trendData.map(item => ({
      date: item.date,
      formattedDate: new Date(item.date).toLocaleDateString('es-CL', {
        day: '2-digit',
        month: '2-digit'
      }),
      ...item
    }));
  };

  const chartData = formatData(data);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3">
          <p className="font-semibold mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: <span className="font-medium">{entry.value.toLocaleString()}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const calculateTrend = (metric) => {
    if (!chartData || chartData.length < 2) return { direction: 'stable', percentage: 0 };

    const values = chartData.map(item => item[metric] || 0);
    const firstHalf = values.slice(0, Math.floor(values.length / 2));
    const secondHalf = values.slice(Math.floor(values.length / 2));

    const firstAvg = firstHalf.reduce((sum, val) => sum + val, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((sum, val) => sum + val, 0) / secondHalf.length;

    if (firstAvg === 0) return { direction: 'stable', percentage: 0 };

    const percentageChange = ((secondAvg - firstAvg) / firstAvg) * 100;
    
    let direction = 'stable';
    if (percentageChange > 5) direction = 'up';
    else if (percentageChange < -5) direction = 'down';

    return {
      direction,
      percentage: Math.abs(percentageChange)
    };
  };

  const renderTrendIndicators = () => {
    if (!chartData || chartData.length === 0) return null;

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {metrics.map(metric => {
          const config = metricConfig[metric];
          const trend = calculateTrend(metric);
          const latestValue = chartData[chartData.length - 1]?.[metric] || 0;

          const TrendIcon = trend.direction === 'up' ? TrendingUp : 
                           trend.direction === 'down' ? TrendingDown : Minus;

          return (
            <div key={metric} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {config.label}
                </p>
                <p className="text-lg font-bold text-foreground">
                  {latestValue.toLocaleString()}
                </p>
              </div>
              <div className="flex items-center space-x-1">
                <TrendIcon 
                  className={`h-4 w-4 ${
                    trend.direction === 'up' ? 'text-green-500' :
                    trend.direction === 'down' ? 'text-red-500' : 'text-muted-foreground'
                  }`}
                />
                <span className={`text-xs font-medium ${
                  trend.direction === 'up' ? 'text-green-500' :
                  trend.direction === 'down' ? 'text-red-500' : 'text-muted-foreground'
                }`}>
                  {trend.percentage.toFixed(1)}%
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>Tendencias de Métricas</span>
          </CardTitle>
          <CardDescription>
            Evolución temporal de las métricas principales
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>Tendencias de Métricas</span>
          </CardTitle>
          <CardDescription>
            Evolución temporal de las métricas principales
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-80 flex items-center justify-center text-muted-foreground">
            No hay datos disponibles para el período seleccionado
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <TrendingUp className="h-5 w-5" />
          <span>Tendencias de Métricas</span>
        </CardTitle>
        <CardDescription>
          Evolución temporal de las métricas principales ({timeframe})
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Gráfico de líneas */}
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="formattedDate" 
                  tick={{ fontSize: 12 }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                {showLegend && <Legend />}
                
                {metrics.map(metric => {
                  const config = metricConfig[metric];
                  return (
                    <Line
                      key={metric}
                      type="monotone"
                      dataKey={metric}
                      stroke={config.color}
                      strokeWidth={config.strokeWidth}
                      name={config.label}
                      dot={{ fill: config.color, strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: config.color, strokeWidth: 2 }}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Indicadores de tendencia */}
          {renderTrendIndicators()}
        </div>
      </CardContent>
    </Card>
  );
};

export default MetricsTrendChart;